// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — bootstrap and the screen machine. Owns which screen is up, the room's Net (created ONCE
// per session, never left for a rematch), the tap input, and the rAF loop that ticks the clock and
// paints. Rules live in game.ts, the cairn in render.ts, the race in race.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds, type RoundPlayer } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';

import { cairnLabel, escapeHtml, fmtTime, scoreLabel } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { createGame, draw as drawGame, drainEvents, take as takeGame, tick as tickGame, type GameEvent, type GameState } from './game';
import { dailySeed, todayUTC, newSeed, seedToCode } from './seed';
import { Board } from './render';
import { createTapInput, type TapInput } from './input';
import { Race, seatOf, createOneShot, type RaceMsg } from './race';
import { SmartBot } from './bot';
import { runCountdown } from './countdown';
import { aboutHtml, ATTRIB, helpHtml, menuHtml, multiResultsHtml, soloResultsHtml } from './ui';

const SLUG = 'cairn';

// ── shell ─────────────────────────────────────────────────────────────────────
const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

// ── persistent session state ───────────────────────────────────────────────────
const playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;

const bestKey = (id: string): string => `best_${id}`;
function bestFor(id: string): number | null {
  const v = store.get<number>(bestKey(id), -1) ?? -1;
  return v < 0 ? null : v;
}
/** Best = highest score (higher is better in every mode). */
function recordBest(mode: Mode, val: number): boolean {
  const cur = bestFor(mode.id);
  if (cur === null || val > cur) {
    store.set(bestKey(mode.id), val);
    return true;
  }
  return false;
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let race: Race | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let roomCode = '';

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, best: bestFor(myModeChoice), muted: sfx.muted() });

  content.querySelectorAll<HTMLElement>('.chip').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      try {
        sfx.play('select');
      } catch {
        /* best-effort */
      }
      showMenu();
    }),
  );
  content.querySelector('#playDaily')!.addEventListener('click', () => startSolo(true));
  content.querySelector('#playRandom')!.addEventListener('click', () => startSolo(false));
  content.querySelector('#playFriends')!.addEventListener('click', () => void beginFriends());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── juice ──────────────────────────────────────────────────────────────────────
function juice(events: GameEvent[]): void {
  for (const e of events) {
    try {
      if (e.t === 'take') sfx.play('coin', { pitch: 0.85 + Math.min(1, (e.heat - 1) / 5) * 0.9 });
      else if (e.t === 'draw') sfx.play('blip', { pitch: 0.6 });
      else if (e.t === 'reject') sfx.play('thud', { pitch: 0.8 });
      else if (e.t === 'clearCairn') sfx.play('win');
      else if (e.t === 'next') sfx.play('go');
      else if (e.t === 'buried') sfx.play('lose');
      else if (e.t === 'timeout') sfx.play('win');
    } catch {
      /* audio best-effort */
    }
    if (!reducedMotion && 'vibrate' in navigator) {
      try {
        if (e.t === 'take' && e.heat >= 3) navigator.vibrate(8);
        else if (e.t === 'clearCairn') navigator.vibrate([10, 20, 10]);
        else if (e.t === 'buried') navigator.vibrate([20, 40]);
      } catch {
        /* haptics best-effort */
      }
    }
  }
}

// ── the game screen (shared by solo + multiplayer) ────────────────────────────────
interface GameConfig {
  mode: Mode;
  state: () => GameState;
  take: (id: number) => GameEvent[];
  draw: () => GameEvent[];
  advance: (dtMs: number) => GameEvent[];
  isOver: () => boolean;
  onOver: () => void;
  onQuit: () => void;
  allowRestart: boolean;
  onRestart?: () => void;
  rivals?: () => Array<{ name: string; score: number; cleared: number; cairns: number; done: boolean; won: boolean }>;
}
interface GameScreen {
  destroy(): void;
}

// Exposed only so the browser-verification hook can drive the REAL take/draw path synchronously
// (rAF is throttled in a hidden tab). Never read by gameplay.
let activeTake: ((id: number) => void) | null = null;
let activeDraw: (() => void) | null = null;
let activeBoard: Board | null = null;
let activeLive = false;

function buildGameScreen(cfg: GameConfig): GameScreen {
  const mode = cfg.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <span class="hud-timer" id="hudTimer"></span>
        <span class="hud-extra" id="hudExtra"></span>
        <button class="iconbtn" id="pause" title="Pause">❚❚</button>
        <button class="linkbtn hud-quit" id="quit">Quit</button>
      </div>
      <div class="timer-wrap"><span class="timer-bar" id="timerBar"></span></div>
      <div class="rivals" id="rivals" hidden></div>
      <div class="board-host" id="boardHost">
        <canvas id="cv"></canvas>
      </div>
      <p class="hint" id="hint">Tap a stone one rune from your hand. Tap the scree to draw.</p>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#cv')!;
  const hudTimer = content.querySelector<HTMLElement>('#hudTimer')!;
  const hudExtra = content.querySelector<HTMLElement>('#hudExtra')!;
  const timerBar = content.querySelector<HTMLElement>('#timerBar')!;
  const rivalsEl = content.querySelector<HTMLElement>('#rivals')!;
  content.querySelector('#quit')!.addEventListener('click', cfg.onQuit);

  const board = new Board(canvas);
  activeBoard = board;
  board.setBoard(cfg.state());
  const doResize = (): void => {
    board.resize();
  };
  requestAnimationFrame(doResize);
  window.addEventListener('resize', doResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(doResize) : null;
  ro?.observe(boardHost);

  let live = false;
  let paused = false;
  let overHandled = false;
  let destroyed = false;
  let last = performance.now();
  let raf = 0;
  let hudTick = 0;

  const doTake = (id: number): void => {
    if (destroyed || !live || paused || cfg.isOver()) return;
    const ev = cfg.take(id);
    board.onEvents(ev, cfg.state(), reducedMotion);
    juice(ev);
    updateHud(cfg.state());
  };
  const doDraw = (): void => {
    if (destroyed || !live || paused || cfg.isOver()) return;
    const ev = cfg.draw();
    board.onEvents(ev, cfg.state(), reducedMotion);
    juice(ev);
    updateHud(cfg.state());
  };
  activeTake = doTake;
  activeDraw = doDraw;

  const input: TapInput = createTapInput(boardHost, {
    stoneAt: (x, y) => board.stoneAt(x, y, cfg.state()),
    stockAt: (x, y) => board.stockAt(x, y),
    exposedCenters: () => board.exposedCenters(cfg.state()),
    onTake: (id) => doTake(id),
    onDraw: () => doDraw(),
    onSelect: (id) => board.setSelected(id),
  });

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    activeLive = true;
    last = performance.now();
  });

  function updateHud(state: GameState): void {
    if (mode.stream) {
      const remain = Math.max(0, mode.limitMs - state.elapsedMs);
      hudTimer.textContent = fmtTime(remain);
      hudExtra.textContent = `${scoreLabel(state.score)} · ${cairnLabel(state.cairns)}`;
      const frac = 1 - state.elapsedMs / mode.limitMs;
      timerBar.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
      timerBar.classList.toggle('low', frac <= 0.25);
    } else {
      const left = Math.max(0, state.board.drawBudget - state.draws);
      hudTimer.textContent = scoreLabel(state.score);
      hudExtra.textContent = `${state.cleared}/${state.board.n} · ${left} draws`;
      const frac = state.board.n > 0 ? state.cleared / state.board.n : 0;
      timerBar.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
      timerBar.classList.toggle('low', mode.lethal && left <= 2);
    }
    if (cfg.rivals) {
      const rs = cfg.rivals();
      if (rs.length) {
        rivalsEl.hidden = false;
        rivalsEl.innerHTML = rs
          .map((r) => {
            const prog = r.done ? (r.won ? '✓' : '—') : '';
            return `<span class="rival${r.done ? ' done' : ''}">${escapeHtml(r.name)} <b>${scoreLabel(r.score)}${prog}</b></span>`;
          })
          .join('');
      }
    }
  }

  function loop(now: number): void {
    if (destroyed) return;
    const dt = Math.min(120, now - last);
    last = now;
    const state = cfg.state();
    if (live && !paused && !cfg.isOver()) {
      const ev = cfg.advance(dt);
      board.onEvents(ev, state, reducedMotion);
      juice(ev);
    }
    board.frame(state, { dtMs: dt, reducedMotion });
    if (++hudTick % 3 === 0) updateHud(state);
    if (cfg.isOver() && !overHandled) {
      overHandled = true;
      updateHud(state);
      setTimeout(() => {
        if (!destroyed) cfg.onOver();
      }, 720);
    }
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);
  updateHud(cfg.state());

  function setPaused(p: boolean): void {
    if (destroyed || cfg.isOver()) return;
    paused = p;
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (p) {
      if (!ov) {
        ov = document.createElement('div');
        ov.className = 'pause-ov';
        ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
          <button class="btn primary" id="resume">Resume</button>
          ${cfg.allowRestart ? '<button class="btn" id="restart">Restart</button>' : ''}
          <button class="btn ghost" id="pquit">Quit</button></div>`;
        boardHost.appendChild(ov);
        ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
        ov.querySelector('#restart')?.addEventListener('click', () => cfg.onRestart?.());
        ov.querySelector('#pquit')!.addEventListener('click', cfg.onQuit);
      }
    } else {
      ov?.remove();
      last = performance.now();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!paused));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!paused);
    else if ((e.key === 'r' || e.key === 'R') && cfg.allowRestart) cfg.onRestart?.();
  }
  window.addEventListener('keydown', onKey);

  return {
    destroy() {
      destroyed = true;
      activeLive = false;
      if (activeTake === doTake) activeTake = null;
      if (activeDraw === doDraw) activeDraw = null;
      if (activeBoard === board) activeBoard = null;
      cancelAnimationFrame(raf);
      cd.cancel();
      input.dispose();
      window.removeEventListener('resize', doResize);
      window.removeEventListener('keydown', onKey);
      ro?.disconnect();
      board.destroy();
    },
  };
}

// ── solo ──────────────────────────────────────────────────────────────────────────
let soloState: GameState | null = null;
let soloDaily = false;
let soloScreen: GameScreen | null = null;

function makeSoloConfig(mode: Mode, onRestart: () => void): GameConfig {
  return {
    mode,
    state: () => soloState!,
    take: (id) => {
      takeGame(soloState!, id);
      return drainEvents(soloState!);
    },
    draw: () => {
      drawGame(soloState!);
      return drainEvents(soloState!);
    },
    advance: (dt) => {
      tickGame(soloState!, dt);
      return drainEvents(soloState!);
    },
    isOver: () => soloState!.over,
    onOver: () => endSolo(),
    onQuit: () => showMenu(),
    allowRestart: true,
    onRestart,
  };
}

function startSolo(daily: boolean): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = daily;
  const seed = daily ? dailySeed(mode.id, todayUTC()) : newSeed();
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen(makeSoloConfig(mode, () => startSolo(soloDaily)));
  onCleanup(() => soloScreen?.destroy());
}

function startSeed(seed: number): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  soloDaily = false;
  soloState = createGame(mode, seed);
  soloScreen = buildGameScreen(makeSoloConfig(mode, () => startSolo(false)));
  onCleanup(() => soloScreen?.destroy());
}

function endSolo(): void {
  const st = soloState!;
  const isNewBest = recordBest(st.mode, st.score);
  showSoloResults(isNewBest);
}

function showSoloResults(isNewBest: boolean): void {
  teardown();
  setPlaying(false);
  const st = soloState!;
  const mode = st.mode;
  content.innerHTML = `
    ${soloResultsHtml({
      mode,
      reason: st.reason,
      won: st.finished && !mode.stream,
      score: st.score,
      cleared: st.cleared,
      total: st.board.n,
      cairns: st.cairns,
      bestChain: st.bestChain,
      best: bestFor(mode.id),
      daily: soloDaily,
      isNewBest,
    })}
    <div class="menu-actions">
      <button class="btn primary" id="again">Play again</button>
      <button class="btn" id="share">Share ${mode.stream ? 'run' : 'result'}</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  content.querySelector('#again')!.addEventListener('click', () => startSolo(soloDaily));
  content.querySelector('#menu')!.addEventListener('click', showMenu);
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  content.querySelector('#share')!.addEventListener('click', () => void shareSolo(st, mode));
}

function shareSolo(st: GameState, mode: Mode): Promise<void> {
  const head = soloDaily ? `Cairn ${todayUTC()} · ${mode.name}` : `Cairn · ${mode.name}`;
  const line = mode.stream
    ? `banked ${cairnLabel(st.cairns)} for ${scoreLabel(st.score)}`
    : st.finished
      ? `cleared the cairn for ${scoreLabel(st.score)}`
      : `scored ${scoreLabel(st.score)}`;
  const link = soloDaily ? 'https://cairn.benrichardson.dev' : `https://cairn.benrichardson.dev/?seed=${seedToCode(st.seed)}&m=${mode.id}`;
  return share(`${head} — ${line}\n${link}`);
}

async function share(text: string): Promise<void> {
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

// ── multiplayer ───────────────────────────────────────────────────────────────────
let netSend: (m: RaceMsg) => void = () => {};
let stOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: GameScreen | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function beginFriends(): void {
  showRoomEntry();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Everyone plays the same cairn. Start a room, or type a friend’s code.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  race = null;
  soloState = null; // a live room supersedes any lingering solo game (keeps the test hook honest)

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        race?.setHost(isSelfHost);
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) race?.peerLeft(seat);
        repaintLobby();
        refreshMultiResults();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
    },
  });
  rounds = r;

  const sendStatus = n.channel<RaceMsg>('st', (data, from) => {
    if (!race) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    race.receive(data, seat);
    refreshMultiResults();
  });
  netSend = (m) => sendStatus(m);
  stOff = () => sendStatus.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 6,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  if (tally.length !== roster.length) tally = Array(roster.length).fill(0);
  const mode = modeOf(info.opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;
  const names = roster.map((p) => p.name);

  const rc = new Race({
    mode,
    seed: info.seed,
    selfSeat,
    names,
    isHost: info.isHost,
    deps: {
      send: (m) => netSend(m),
      onChange: () => refreshMultiResults(),
      onSelfEnd: () => {
        rounds?.finish();
        setTimeout(() => showMultiResults(), 760);
      },
    },
  });
  race = rc;
  setPlaying(true);

  if (selfSeat === null) {
    showMultiResults();
    return;
  }

  gameScreen = buildGameScreen({
    mode,
    state: () => rc.state,
    take: (id) => rc.take(id),
    draw: () => rc.draw(),
    advance: (dt) => rc.tick(dt),
    isOver: () => rc.state.over,
    onOver: () => {
      /* onSelfEnd already scheduled the results */
    },
    // Quitting mid-round: end this peer's round FIRST (phase playing → waiting) so the
    // lobby renders — the engine paints nothing for a peer still seated in a live round
    // ("the game owns the screen"), which would otherwise show a blank lobby.
    onQuit: () => {
      rounds?.finish();
      backToLobby();
    },
    allowRestart: false,
    rivals: () =>
      [...rc.results.values()]
        .filter((p) => p.seat !== rc.selfSeat && !p.left)
        .map((p) => ({ name: p.name, score: p.score, cleared: p.cleared, cairns: p.cairns, done: p.done, won: p.won })),
  });
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
}

// ── multiplayer results ──────────────────────────────────────────────────────────
let resultsMounted = false;
let winsAwarded = false;
const closeWait = createOneShot(25_000, () => refreshMultiResults());

function showMultiResults(): void {
  teardown();
  setPlaying(false);
  resultsMounted = true;
  winsAwarded = false;
  content.innerHTML = `
    <section class="screen results" id="mres"></section>
    <div class="menu-actions" id="mact">
      <button class="btn primary" id="again">Play again</button>
      <p class="waiting-note" id="waiting" aria-live="polite"></p>
      <button class="btn" id="lobby">Back to lobby</button>
      <button class="btn ghost" id="menu">Main menu</button>
      <button class="btn ghost" id="feedback">Report a problem</button>
    </div>`;
  const again = content.querySelector<HTMLButtonElement>('#again')!;
  again.addEventListener('click', () => {
    rounds?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });
  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  content.querySelector('#menu')!.addEventListener('click', () => void leaveRoom());
  content.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  onCleanup(() => {
    resultsMounted = false;
    closeWait.cancel();
  });
  closeWait.poke();
  refreshMultiResults();
}

function refreshMultiResults(): void {
  if (!resultsMounted || !race) return;
  const rc = race;
  const box = content.querySelector<HTMLElement>('#mres');
  if (!box) return;

  if (!winsAwarded && rc.everyoneDone()) {
    winsAwarded = true;
    const w = rc.winner();
    if (w) tally[w.seat] = (tally[w.seat] ?? 0) + 1;
  }

  box.innerHTML = multiResultsHtml(rc.ranked(), rc.selfSeat, rc.mode, tally);

  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (waiting) {
    const r = rounds?.state();
    if (r && r.startsInMs !== null) {
      waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
    } else if (!rc.everyoneDone()) {
      const left = [...rc.results.values()].filter((x) => !x.done && !x.left).map((x) => x.name);
      waiting.textContent = left.length ? `Still playing: ${left.join(', ')}` : '';
    } else if (r && r.votes.length) {
      waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  }
}

function backToLobby(): void {
  teardown();
  race = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  race = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    stOff();
  } catch {
    /* channel may already be gone */
  }
  stOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  const seedParam = params.get('seed');
  if (room && normalizeRoomCode(room).length >= 3) {
    beginFriends();
  } else if (seedParam) {
    const m = params.get('m');
    if (m) myModeChoice = modeOf(m).id;
    const seedNum = parseInt(seedParam.trim().toLowerCase(), 36);
    if (Number.isFinite(seedNum) && seedNum > 0) {
      clearRoomInUrl();
      startSeed(seedNum >>> 0);
      return;
    }
    showMenu();
  } else {
    showMenu();
  }
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// The browser-verification pass drives this to prove the take → chain → clear wiring end-to-end
// and to read state (test-only).
(window as unknown as { __cairn?: unknown }).__cairn = {
  state(): unknown {
    const s = race?.state ?? soloState;
    if (!s) return null;
    return {
      score: s.score,
      cleared: s.cleared,
      totalCleared: s.totalCleared,
      cairns: s.cairns,
      draws: s.draws,
      drawBudget: s.board.drawBudget,
      hand: s.hand,
      over: s.over,
      finished: s.finished,
      reason: s.reason,
      index: s.index,
      bestChain: s.bestChain,
      ms: s.elapsedMs,
    };
  },
  mode(): string {
    return (race?.state ?? soloState)?.mode.id ?? '';
  },
  live(): boolean {
    return activeLive;
  },
  n(): number {
    return (race?.state ?? soloState)?.board.n ?? 0;
  },
  /** Exposed, legal stone ids right now (so the browser can click a real legal stone). */
  legal(): number[] {
    const s = race?.state ?? soloState;
    if (!s) return [];
    const out: number[] = [];
    for (let id = 0; id < s.present.length; id++) {
      if (!s.present[id]) continue;
      const exposed = s.board.coveredBy[id].every((c) => !s.present[c]);
      const d = (((s.board.stones[id].rank - s.hand) % 13) + 13) % 13;
      if (exposed && (d === 1 || d === 12)) out.push(id);
    }
    return out;
  },
  take(id: number): void {
    activeTake?.(id);
  },
  draw(): void {
    activeDraw?.();
  },
  /** Solve the CURRENT cairn through the shipped take/draw path. */
  solveNow(): unknown {
    const s = race?.state ?? soloState;
    if (!s) return null;
    const bot = new SmartBot();
    let guard = 0;
    while (!s.over && guard++ < 800) {
      const a = bot.next(s);
      if (!a) break;
      if (a.k === 'draw') activeDraw?.();
      else activeTake?.(a.id);
    }
    return (window as unknown as { __cairn: { state(): unknown } }).__cairn.state();
  },
  stonePixel(id: number): { x: number; y: number } | null {
    return activeBoard?.stonePixel(id) ?? null;
  },
  handPixel(): { x: number; y: number } | null {
    return activeBoard?.handPixel() ?? null;
  },
  emptyPixel(): { x: number; y: number } | null {
    return activeBoard?.emptyPixel() ?? null;
  },
};

boot();
